// SHK App – GitHub sync
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";
// Eager: Login + Home — die ersten Screens sollen sofort da sein
import Login from "./pages/Login";
import Home from "./pages/Home";

/**
 * lazyWithRetry — wie React.lazy, fängt aber fehlgeschlagene dynamische Imports ab.
 * Ursache in der Praxis: nach einem Deploy ändern sich die Chunk-Dateinamen (Hash).
 * Ein Browser, der die App VOR dem Deploy offen hatte, versucht einen alten Chunk zu
 * laden, den es nicht mehr gibt → Import wirft → ohne Behandlung wird die ganze App
 * weiß und der User muss manuell neu laden. Hier: einmal automatisch neu laden
 * (holt frische index.html + neue Chunks). sessionStorage-Flag verhindert Reload-Schleifen.
 */
function lazyWithRetry<T extends { default: React.ComponentType<unknown> }>(
  factory: () => Promise<T>
) {
  return lazy(async () => {
    const FLAG = "zos:chunk-reloaded";
    try {
      const mod = await factory();
      window.sessionStorage.removeItem(FLAG);
      return mod;
    } catch (err) {
      if (!window.sessionStorage.getItem(FLAG)) {
        window.sessionStorage.setItem(FLAG, "1");
        window.location.reload();
        // Reload läuft — Component nie rendern lassen, bis die Seite neu lädt
        return new Promise<T>(() => {});
      }
      throw err;
    }
  });
}

// Lazy: alles andere wird erst geladen wenn die Route besucht wird (Code-Splitting,
// 09.06. — vorher lud das eine 873-kB-Bundle alles inkl. Settings/Editoren beim Login)
const LeckortungNew = lazyWithRetry(() => import("./pages/LeckortungNew"));
const TagesberichtNew = lazyWithRetry(() => import("./pages/TagesberichtNew"));
const Berichte = lazyWithRetry(() => import("./pages/Berichte"));
const BerichtDetail = lazyWithRetry(() => import("./pages/BerichtDetail"));
const Profil = lazyWithRetry(() => import("./pages/Profil"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Einstellungen = lazyWithRetry(() => import("./pages/Einstellungen"));
const Controlling = lazyWithRetry(() => import("./pages/Controlling"));
const ControllingProjekt = lazyWithRetry(() => import("./pages/ControllingProjekt"));
const ControllingRanking = lazyWithRetry(() => import("./pages/ControllingRanking"));
const EinstellungenSaetze = lazyWithRetry(() => import("./pages/EinstellungenSaetze"));
const EinstellungenZugang = lazyWithRetry(() => import("./pages/EinstellungenZugang"));
const QuestionsEditor = lazyWithRetry(() => import("./pages/QuestionsEditor"));
const Leads = lazyWithRetry(() => import("./pages/Leads"));
const LeadDetail = lazyWithRetry(() => import("./pages/LeadDetail"));
const LeadAudit = lazyWithRetry(() => import("./pages/LeadAudit"));
const AuditNew = lazyWithRetry(() => import("./pages/AuditNew"));
const Feedback = lazyWithRetry(() => import("./pages/Feedback"));
const Buerobericht = lazyWithRetry(() => import("./pages/Buerobericht"));
const Aufgaben = lazyWithRetry(() => import("./pages/Aufgaben"));
const Beobachtungen = lazyWithRetry(() => import("./pages/Beobachtungen"));
const Meldungen = lazyWithRetry(() => import("./pages/Meldungen"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function RouteFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <ErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/home" replace /> : <Login />}
      />
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? "/home" : "/login"} replace />}
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leckortung/new"
        element={
          <ProtectedRoute bereich='leckortung'>
            <LeckortungNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tagesbericht/new"
        element={
          <ProtectedRoute bereich='tagesbericht'>
            <TagesberichtNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/berichte"
        element={
          <ProtectedRoute bereich='berichte'>
            <Berichte />
          </ProtectedRoute>
        }
      />
      <Route
        path="/berichte/:id"
        element={
          <ProtectedRoute bereich='berichte'>
            <BerichtDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute bereichAny={['feedback_kundendienst', 'feedback_projektleitung', 'feedback_buero', 'feedback_gf']}>
            <Feedback />
          </ProtectedRoute>
        }
      />
      {/* Buerotaetigkeits-Dokumentation (PC-33/34/35). Der Bereich
          `buerobericht` ist die einzige Steuerung — alle drei Endpunkte
          antworten ohne ihn mit 403. */}
      <Route
        path="/buerobericht"
        element={
          <ProtectedRoute bereich='buerobericht'>
            <Buerobericht />
          </ProtectedRoute>
        }
      />
      {/* Nur Fachbereich `gf` — das Backend (PC-31) antwortet allen anderen
          ohnehin mit 403, hier nur damit niemand auf einer leeren Seite
          landet. `nurFachbereich` ist absichtlich nicht fail-open. */}
      <Route
        path="/beobachtungen"
        element={
          <ProtectedRoute nurFachbereich={['gf']}>
            <Beobachtungen />
          </ProtectedRoute>
        }
      />
      {/* Meldeuebersicht (PC-32) — wie /beobachtungen nur Fachbereich `gf`. */}
      <Route
        path="/meldungen"
        element={
          <ProtectedRoute nurFachbereich={['gf']}>
            <Meldungen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/aufgaben"
        element={
          <ProtectedRoute>
            <Aufgaben />
          </ProtectedRoute>
        }
      />
      {/* Dashboard ist als "Übersicht"-Reiter in /leads aufgegangen (06.09.2026) — alter Link/Bookmark leitet einfach dorthin. */}
      <Route path="/dashboard" element={<Navigate to="/leads" replace />} />
      <Route
        path="/profil"
        element={
          <ProtectedRoute>
            <Profil />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute bereichAny={['leads', 'dashboard']}>
            <Leads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <ProtectedRoute bereichAny={['leads', 'dashboard']}>
            <LeadDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id/audit"
        element={
          <ProtectedRoute bereich='audit'>
            <LeadAudit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit/new"
        element={
          <ProtectedRoute bereich='audit'>
            <AuditNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/controlling"
        element={
          <ProtectedRoute bereich='controlling'>
            <Controlling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/controlling/ranking"
        element={
          <ProtectedRoute bereich='controlling'>
            <ControllingRanking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/controlling/:id"
        element={
          <ProtectedRoute bereich='controlling'>
            <ControllingProjekt />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['geschaeftsfuehrung']} bereich='einstellungen'>
            <Einstellungen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/leads"
        element={
          <ProtectedRoute allowedRoles={['geschaeftsfuehrung']} bereich='einstellungen'>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/zugang"
        element={
          <ProtectedRoute allowedRoles={['geschaeftsfuehrung']} bereich='einstellungen'>
            <EinstellungenZugang />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/controlling/saetze"
        element={
          <ProtectedRoute allowedRoles={['geschaeftsfuehrung']} bereich='einstellungen'>
            <EinstellungenSaetze />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/questions"
        element={
          <ProtectedRoute allowedRoles={['geschaeftsfuehrung']} bereich='einstellungen'>
            <QuestionsEditor />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
